import React, { useState, useEffect } from 'react'
import { MainDrawer } from './components/main-drawer/main-drawer'
import classNames from 'classnames'

import './main.scss'
import { MainDrawerSlim } from './components/main-drawer/main-drawer-slim'
import { DatePicker } from './components/date-time-picker'
import CloseIcon from '@mui/icons-material/Close'
import { translate as _ } from './services/translations'
import { TButton } from './components/button'
import { EventContainer } from './components/event-container/event-container'
import { fetchEventsFromDay } from './services/fetching/fetching'
import {
  getEventById,
  getEventsToExportById,
  sortEventsByTime
} from './components/event-container/utils'
import { ExportPage, FilterPage, SettingsPage, ViewsPage } from './pages'
import { viewTypes } from './pages/views-page/constants'
import PropTypes from 'prop-types'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { EventEditorPage } from './pages/event-editor-page'
import { updateEvents } from './services/redux-reducers/application/application-reducer'
import { logUserAction } from './services/redux-reducers/user-settings/user-settings-reducer'
import { Toast, TOAST_SEVERITY } from './components/toast'
import { useSessionKeepAlive } from './services/session'

export const Main = ({ onLogout }) => {
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [fetching, setFetching] = useState(true)
  const [filters, setFilters] = useState([])
  const [tagsFilters, setTagsFilters] = useState({})
  const [view, setView] = useState(viewTypes.DAY)
  const [viewDates, setViewDates] = useState(null)
  const [currentPage, setCurrentPage] = useState('events')
  const [toastOpened, setToastOpened] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState(TOAST_SEVERITY.SUCCESS)

  const [editedEvent, setEditedEvent] = useState(null)
  const [fetchTimestamp, setFetchTimestamp] = useState(new Date().getTime())

  const store = useStore()
  const dispatch = useDispatch()
  const level = store.getState().userSettings.level
  const exportedEvents = useSelector((state) => state.application.exportedEvents)
  const events = useSelector((state) => state.application.events)

  const adminZoneLabel = _(['mainDrawer', 'adminZone'])

  const handleOnLogout = () => {
    dispatch(logUserAction({ a: 'logout' }))
    onLogout()
  }

  const handleOnSessionExpired = () => {
    dispatch(logUserAction({ a: 'session_expired' }))
    onLogout('session-expired')
  }

  const handleOnDrawerClose = (automatic) => {
    setDrawerOpened(automatic && window.innerWidth > 800)
  }

  const handleOnDrawerOpen = () => {
    setDrawerOpened(true)
  }

  const handleOnSelectedDateChange = (newValue) => {
    setSelectedDate(newValue)
    setView(viewTypes.DAY)
    setFetching(true)
    setDrawerOpened(window.innerWidth > 800)
    dispatch(logUserAction({ a: 'fetch_events', v: { day: newValue.toISOString() } }))
    fetchEventsFromDay(newValue).then((result) => {
      setFetching(false)
      // setEvents(sortEventsByTime(result.data))
      dispatch(updateEvents(sortEventsByTime(result.data)))
      setTimeout(() => {
        setFetchTimestamp(new Date().getTime())
      }, 500)
    })
  }

  const handleOnManualClick = () => {
    const link = document.createElement('a')
    link.download = 'manual.pdf'
    link.href = 'https://kalendarium.tasr.sk/manual.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOnPageChange = (newPage) => {
    setCurrentPage(newPage)
    handleOnDrawerClose(true)
  }

  const handleOnFiltersApply = (newFilters, newTagsFilters) => {
    setFilters(newFilters)
    setTagsFilters(newTagsFilters)
    setCurrentPage('events')
  }

  const handleOnFilterRemove = (newFilters, newTagsFilters) => {
    setFilters(newFilters)
    setTagsFilters(newTagsFilters)
  }

  const handleOnViewApply = (newView, newViewDates) => {
    setView(newView)
    setViewDates(newViewDates)
    setFetching(true)
    dispatch(
      logUserAction({ a: 'fetch_events', v: { from: newViewDates.from, to: newViewDates.to } })
    )
    fetchEventsFromDay(
      selectedDate,
      newViewDates.from?.toISOString(),
      newViewDates.to?.toISOString()
    ).then((result) => {
      setFetching(false)
      dispatch(updateEvents(sortEventsByTime(result.data)))
      setFetchTimestamp(new Date().getTime())
    })
    setCurrentPage('events')
  }

  const handleOnSyncClick = () => {
    handleOnSelectedDateChange(selectedDate)
  }

  const handleOnServicesClick = () => {
    dispatch(logUserAction({ a: 'etasr_clicked' }))
    window.open('https://etasr.sk/login', '_blank')
  }

  const handleOnAdminClick = () => {
    dispatch(logUserAction({ a: 'admin_zone_clicked' }))
    window.open('https://kalendarium.tasr.sk/admin/', '_blank')
  }

  const handleOnAddEventClick = () => {
    setEditedEvent(null)
    dispatch(logUserAction({ a: 'event_editor_page_clicked', v: { action: 'add' } }))
    setCurrentPage('event-editor')
  }

  const handleOnEditEventClick = (id) => {
    dispatch(logUserAction({ a: 'event_editor_page_clicked', v: { action: 'edit', id } }))
    setEditedEvent(getEventById(events, id))
    setCurrentPage('event-editor')
  }

  const handleOnEventAdded = () => {
    setCurrentPage('events')
    handleOnSyncClick()
  }

  const handleOnEventUpdated = () => {
    setCurrentPage('events')
    handleOnSyncClick()
  }

  const handleOnEventRemoved = () => {
    setCurrentPage('events')
    handleOnSyncClick()
  }

  const handleOnEventRestored = () => {
    setCurrentPage('events')
    handleOnSyncClick()
  }

  const handleOnEventEditorDismissed = () => {
    setCurrentPage('events')
    handleOnSyncClick()
  }

  const handleOnToast = (message, severity = TOAST_SEVERITY.SUCCESS) => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpened(true)
  }

  const handleOnToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setToastOpened(false)
  }

  useSessionKeepAlive({ onSessionExpired: handleOnSessionExpired })

  useEffect(() => {
    handleOnSelectedDateChange(selectedDate)
    if (window.innerWidth > 800) {
      setDrawerOpened(true)
    }
  }, [])

  return (
    <div className="main">
      <MainDrawer onClose={handleOnDrawerClose} isOpened={drawerOpened}>
        <div>
          <TButton
            onClick={handleOnDrawerClose}
            className="close-panel-button"
            id="close-panel-button">
            <CloseIcon />
            <div className="close-panel-text">
              {_(['mainDrawer', 'closePanel'])
                .split()
                .map((letter, idx) => (
                  <span key={`close-panel-text-${idx}`}>{letter}</span>
                ))}
            </div>
          </TButton>
          <DatePicker onChange={handleOnSelectedDateChange} value={selectedDate} />
          <TButton
            onClick={() => handleOnPageChange('filters')}
            id={'menu-button-filters'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'filter'])}
          </TButton>
          <TButton
            onClick={() => handleOnPageChange('views')}
            id={'menu-button-views'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'views'])}
          </TButton>
          <TButton
            onClick={() => handleOnPageChange('export')}
            id={'menu-button-export'}
            disabled={!exportedEvents?.length}
            className="drawer-menu-button">
            {_(['mainDrawer', 'exportEvents'])}
          </TButton>
          <TButton
            onClick={() => {
              dispatch(logUserAction({ a: 'sync_events_clicked' }))
              handleOnSyncClick()
            }}
            id={'menu-button-sync'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'sync'])}
          </TButton>
          <TButton
            onClick={handleOnServicesClick}
            id={'menu-button-back-to-services'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'backToTasr'])}
          </TButton>
          {level === 4 && (
            <TButton
              onClick={handleOnAdminClick}
              id={'menu-button-admin-zone'}
              className="drawer-menu-button">
              {adminZoneLabel}
            </TButton>
          )}

          <TButton
            onClick={() => handleOnPageChange('settings')}
            id={'menu-button-settings'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'settings'])}
          </TButton>

          <TButton
            onClick={() => handleOnManualClick()}
            id={'menu-button-manual'}
            className="drawer-menu-button">
            {_(['mainDrawer', 'manual'])}
          </TButton>
        </div>
      </MainDrawer>
      {!drawerOpened && <MainDrawerSlim onClick={handleOnDrawerOpen} />}
      <div className={classNames('main-content', { 'drawer-opened': drawerOpened })}>
        {currentPage === 'events' && (
          <EventContainer
            day={selectedDate}
            loading={fetching}
            filters={tagsFilters}
            onFilterRemove={handleOnFilterRemove}
            viewType={view}
            viewDates={viewDates}
            onAddEvent={handleOnAddEventClick}
            onEditEvent={handleOnEditEventClick}
            fetchTimestamp={fetchTimestamp}
            onEventRemoved={handleOnEventRemoved}
            onEventRestored={handleOnEventRestored}
            onToast={handleOnToast}
          />
        )}
        {currentPage === 'filters' && (
          <FilterPage
            onFilterApply={handleOnFiltersApply}
            onFilterClose={() => handleOnPageChange('events')}
            initialFilters={filters}
          />
        )}
        {currentPage === 'views' && (
          <ViewsPage
            onViewApply={handleOnViewApply}
            onViewClose={() => handleOnPageChange('events')}
            initialView={view}
            initialFrom={viewDates?.from}
            initialTo={viewDates?.to}
          />
        )}
        {currentPage === 'export' && (
          <ExportPage
            onBack={() => handleOnPageChange('events')}
            events={getEventsToExportById(events, exportedEvents)}
            withDate={view !== viewTypes.DAY}
            filters={tagsFilters}
          />
        )}
        {currentPage === 'settings' && (
          <SettingsPage onBack={() => handleOnPageChange('events')} onLogout={handleOnLogout} />
        )}
        {currentPage === 'event-editor' && (
          <EventEditorPage
            onRemove={handleOnEventRemoved}
            onAdd={handleOnEventAdded}
            onCancel={handleOnEventEditorDismissed}
            event={editedEvent}
            onEdit={handleOnEventUpdated}
            onRestore={handleOnEventRestored}
            onToast={handleOnToast}
          />
        )}
        <Toast
          open={toastOpened}
          message={toastMessage}
          severity={toastSeverity}
          onClose={handleOnToastClose}
        />
      </div>
    </div>
  )
}

Main.propTypes = {
  onLogout: PropTypes.func.isRequired
}
